import React, { useState, useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@you/ui'
import { ICON_OPTIONS } from './icon-options'

/**
 * 图标选择器组件属性
 */
export interface IconSelectorProps {
  /** 当前选中的图标地址 */
  selectedIcon: string
  /** 图标选择回调 */
  onIconSelect: (iconUrl: string) => void
  /** 预览图标尺寸，默认为 48 */
  previewSize?: number
  /** 选择器中的图标尺寸，默认为 48 */
  selectorSize?: number
  /** 预览区域尺寸，默认为 58 */
  previewBoxSize?: number
}

/**
 * 图标选择器组件
 * 支持8种不同图标的平铺选择，点击图标预览区域弹出选择器
 */
const IconSelector: React.FC<IconSelectorProps> = ({
  selectedIcon,
  onIconSelect,
  previewSize = 48,
  selectorSize = 48,
  previewBoxSize = 58,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  /**
   * 处理图标选择
   */
  const handleIconSelect = useCallback(
    (iconUrl: string) => {
      onIconSelect(iconUrl)
      setIsOpen(false)
    },
    [onIconSelect]
  )

  /**
   * 获取当前选中图标的预览地址
   */
  const getSelectedIconPreview = useCallback(() => {
    if (!selectedIcon) return ICON_OPTIONS[0].preview
    const icon = ICON_OPTIONS.find((option) => option.value === selectedIcon)
    return icon?.preview || ICON_OPTIONS[0].preview
  }, [selectedIcon])

  /**
   * 处理预览区域点击
   */
  const handlePreviewClick = useCallback(() => {
    setIsOpen(true)
  }, [])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className="w-12 h-12 rounded-lg border-2 border-gray-200 flex items-center justify-center cursor-pointer hover:border-blue-300 transition-colors overflow-hidden bg-white"
          style={{ width: previewBoxSize, height: previewBoxSize }}
          onClick={handlePreviewClick}
        >
          <img
            src={getSelectedIconPreview()}
            alt="数据集图标"
            className="w-full h-full object-contain p-1"
            style={{ width: previewSize, height: previewSize }}
            onError={(e) => {
              // 图片加载失败时使用默认图标
              const target = e.target as HTMLImageElement
              target.src = ICON_OPTIONS[0].preview
            }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-4" align="start">
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900">选择图标</h4>
          <div className="grid grid-cols-4 gap-3">
            {ICON_OPTIONS.map((icon) => (
              <div
                key={icon.value}
                className={`
                  cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 
                  flex flex-col items-center bg-white
                  ${
                    selectedIcon === icon.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }
                `}
                onClick={() => handleIconSelect(icon.value)}
              >
                <img
                  src={icon.preview}
                  alt={icon.label}
                  className="rounded object-contain p-1"
                  style={{ width: selectorSize, height: selectorSize }}
                  onError={(e) => {
                    // 图片加载失败时使用默认图标
                    const target = e.target as HTMLImageElement
                    target.src = ICON_OPTIONS[0].preview
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default IconSelector
